import type { TitleBrief } from './catalogue.js';

export const LIBRARY_STATUSES = ['planning', 'watching', 'completed', 'paused', 'dropped'] as const;

export type LibraryStatus = (typeof LIBRARY_STATUSES)[number];

export const LIBRARY_STATUS_LABELS_PL: Record<LibraryStatus, string> = {
  planning: 'Planuję',
  watching: 'Oglądam',
  completed: 'Ukończone',
  paused: 'Wstrzymane',
  dropped: 'Porzucone',
};

export interface LibraryEntry {
  titleId: string;
  title: TitleBrief;
  status: LibraryStatus;
  score: number | null;
  rewatches: number;
  notes: string | null;
  favourite: boolean;
  episodesWatched: number;
  startedAt: string | null;
  finishedAt: string | null;
  updatedAt: string;
}

export interface ContinueCard {
  titleId: string;
  title: TitleBrief;
  episodeNumber: number;
  positionSec: number;
  durationSec: number;
  /** 0..1 — pasek na kaflu. */
  fraction: number;
  updatedAt: string;
  /** Gotowy do odtworzenia od razu, bez pobierania. */
  cachedMediaFileId: string | null;
}

/**
 * Kafel „nowy odcinek": seria ze statusem „oglądam", w której źródła wystawiły
 * odcinek późniejszy niż ostatnie oglądanie tej serii.
 *
 * Jeden kafel na SERIĘ, nie na odcinek. Po dwóch tygodniach przerwy trzy nowe
 * odcinki dałyby trzy kafle tego samego tytułu, a włączy się i tak najniższy —
 * dlatego numer wskazuje ten najniższy, a `moreCount` mówi, ile czeka za nim.
 */
export interface NewEpisodeCard {
  titleId: string;
  title: TitleBrief;
  /** Najniższy nowy nieobejrzany odcinek — ten, który się włączy. */
  episodeNumber: number;
  /** Ile nowych czeka poza tym jednym. Zero to zwykły przypadek. */
  moreCount: number;
  /** Kiedy źródło pokazało go pierwszy raz. Po tym sortujemy pas. */
  appearedAt: string;
  /** Skąd — plakietka języka mówi widzowi, czego się spodziewać po napisach. */
  sourceId: string;
  sourceLabel: string;
  language: 'pl' | 'en';
  /** Źródło zapasowe: bierzemy je, gdy nie ma polskiego, i mówimy o tym wprost. */
  fallback: boolean;
  /** Gotowy do odtworzenia od razu, bez pobierania. */
  cachedMediaFileId: string | null;
}

/**
 * Odcinek uznajemy za obejrzany, gdy widz dotarł do napisów końcowych.
 * Goły próg 95% niedoliczałby odcinków, bo endingi w anime mają około 90 sekund.
 */
export const COMPLETION_TAIL_SEC = 90;
export const COMPLETION_FRACTION = 0.92;

export function isEpisodeCompleted(positionSec: number, durationSec: number): boolean {
  if (durationSec <= 0) return false;
  const threshold = Math.min(durationSec - COMPLETION_TAIL_SEC, durationSec * COMPLETION_FRACTION);
  return positionSec >= threshold;
}

/** Minimalny postęp, od którego pozycja pojawia się w „Kontynuuj oglądanie". */
export const CONTINUE_MIN_POSITION_SEC = 30;

export interface WatchStats {
  totalEpisodes: number;
  totalHours: number;
  byGenre: Array<{ genre: string; hours: number }>;
  byStatus: Record<LibraryStatus, number>;
  longestStreakDays: number;
  currentStreakDays: number;
}
